<template>
  <div class="recovery-plan">
    <div class="plan-header">
      <div class="header-content">
        <h3>Recovery Plan</h3>
        <span class="day-label">{{ dayLabel }}</span>
      </div>
      <div v-if="recoveryWindow" class="window-status" :class="recoveryWindow.status">
        {{ recoveryWindow.status === 'active' ? 'WINDOW OPEN' : 'Recovery Day' }}
      </div>
    </div>

    <div v-if="recoveryWindow && recoveryWindow.status === 'active'" class="golden-window">
      <div class="window-icon">
        <span>60</span>
        <span class="unit">min</span>
      </div>
      <div class="window-info">
        <h4>Golden Recovery Window</h4>
        <p>{{ recoveryWindow.message }}</p>
        <div v-if="recoveryWindow.time_remaining" class="countdown">
          <span class="time">{{ recoveryWindow.time_remaining }}</span> remaining
        </div>
      </div>
    </div>

    <div class="recovery-priorities">
      <h4>Recovery Priorities</h4>

      <div class="priority-card carbs">
        <div class="priority-header">
          <span class="priority-icon">C</span>
          <div class="priority-info">
            <h5>Carbohydrate Replenishment</h5>
            <span class="target">Target: 1-1.2g per kg/hour</span>
          </div>
        </div>
        <p class="priority-description">
          Restore muscle glycogen depleted during the match. Focus on high-GI carbs immediately after.
        </p>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: carbsProgress + '%' }"></div>
        </div>
      </div>

      <div class="priority-card protein">
        <div class="priority-header">
          <span class="priority-icon">P</span>
          <div class="priority-info">
            <h5>Protein for Repair</h5>
            <span class="target">Target: 20-25g post-match</span>
          </div>
        </div>
        <p class="priority-description">
          Support muscle repair and reduce soreness. Combine with carbs for optimal absorption.
        </p>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: proteinProgress + '%' }"></div>
        </div>
      </div>

      <div class="priority-card hydration">
        <div class="priority-header">
          <span class="priority-icon">H</span>
          <div class="priority-info">
            <h5>Rehydration</h5>
            <span class="target">Target: 150% of fluid lost</span>
          </div>
        </div>
        <p class="priority-description">
          Replace fluids lost through sweat. Include electrolytes to restore balance.
        </p>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: hydrationProgress + '%' }"></div>
        </div>
      </div>
    </div>

    <div class="recommended-meals">
      <h4>Recommended Recovery Meals</h4>
      <div class="meal-options">
        <div
          v-for="meal in recoveryMeals"
          :key="meal.id"
          class="meal-option"
          @click="selectMeal(meal)"
        >
          <div class="meal-main">
            <span class="meal-name">{{ meal.name }}</span>
            <span class="meal-time">{{ meal.prep_time_mins }} min</span>
          </div>
          <div class="meal-macros">
            <span class="macro carbs">C: {{ meal.carbs }}g</span>
            <span class="macro protein">P: {{ meal.protein }}g</span>
          </div>
          <div v-if="meal.timing_window" class="meal-timing">
            <span class="timing-badge">{{ formatTiming(meal.timing_window) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="recovery-tips">
      <h4>Recovery Tips</h4>
      <div class="tips-list">
        <div class="tip-item">
          <span class="tip-icon">1</span>
          <span class="tip-text">Eat within 60 mins for optimal glycogen synthesis</span>
        </div>
        <div class="tip-item">
          <span class="tip-icon">2</span>
          <span class="tip-text">Include both carbs and protein in your recovery meal</span>
        </div>
        <div class="tip-item">
          <span class="tip-icon">3</span>
          <span class="tip-text">Continue eating every 2-3 hours for the next 6 hours</span>
        </div>
        <div class="tip-item">
          <span class="tip-icon">4</span>
          <span class="tip-text">Aim for 7-9 hours of quality sleep tonight</span>
        </div>
      </div>
    </div>

    <div v-if="isRecoveryDay" class="full-day-guidance">
      <h4>Full Recovery Day Nutrition</h4>
      <div class="guidance-cards">
        <div class="guidance-card">
          <span class="guidance-icon">B</span>
          <div class="guidance-content">
            <h5>Breakfast</h5>
            <p>High protein + carbs to continue recovery</p>
          </div>
        </div>
        <div class="guidance-card">
          <span class="guidance-icon">L</span>
          <div class="guidance-content">
            <h5>Lunch</h5>
            <p>Balanced meal with lean protein and vegetables</p>
          </div>
        </div>
        <div class="guidance-card">
          <span class="guidance-icon">D</span>
          <div class="guidance-content">
            <h5>Dinner</h5>
            <p>Include anti-inflammatory foods (fish, greens)</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Nutrition from "@/services/nutrition-service";

export default {
  name: "RecoveryPlan",

  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
    teamId: {
      type: [Number, String],
      default: null,
    },
    date: {
      type: String,
      default: () => new Date().toISOString().split("T")[0],
    },
    activity: {
      type: Object,
      default: null,
    },
    dailyTotals: {
      type: Object,
      default: () => ({ total_carbs: 0, total_protein: 0, total_hydration: 0 }),
    },
    dailyTargets: {
      type: Object,
      default: () => ({ carbs: 400, protein: 120, hydration: 3000 }),
    },
  },

  data() {
    return {
      recoveryWindow: null,
      recoveryMeals: [],
      loading: false,
    };
  },

  computed: {
    isRecoveryDay() {
      return this.activity && this.activity.context === "recovery_day";
    },

    dayLabel() {
      if (this.isRecoveryDay) return "MD+1 (Recovery)";
      if (this.activity && this.activity.type === "match") return "Post-Match";
      return "Recovery";
    },

    carbsProgress() {
      if (!this.dailyTargets.carbs) return 0;
      return Math.min(100, Math.round((this.dailyTotals.total_carbs / this.dailyTargets.carbs) * 100));
    },

    proteinProgress() {
      if (!this.dailyTargets.protein) return 0;
      return Math.min(100, Math.round((this.dailyTotals.total_protein / this.dailyTargets.protein) * 100));
    },

    hydrationProgress() {
      if (!this.dailyTargets.hydration) return 0;
      return Math.min(100, Math.round((this.dailyTotals.total_hydration / this.dailyTargets.hydration) * 100));
    },
  },

  async mounted() {
    await this.loadRecoveryData();
  },

  methods: {
    async loadRecoveryData() {
      this.loading = true;
      try {
        const response = await Nutrition.getSmartRecommendations(
          this.userId,
          this.teamId,
          this.date
        );

        if (response.data && response.data.success) {
          const data = response.data.data;
          this.recoveryWindow = data.post_match_window || data.recovery_window;
          this.recoveryMeals = (data.recommendations || []).filter(
            (m) => m.meal_type === "post_match" || m.meal_type === "post_training"
          );

          // If no specific recovery meals, get general post-match templates
          if (this.recoveryMeals.length === 0) {
            const templatesResponse = await Nutrition.getFilteredTemplates({
              mealType: "post_match",
              userId: this.userId,
            });
            if (templatesResponse.data && templatesResponse.data.success) {
              this.recoveryMeals = templatesResponse.data.data.slice(0, 4);
            }
          }
        }
      } catch (error) {
        console.error("Error loading recovery data:", error);
      } finally {
        this.loading = false;
      }
    },

    formatTiming(window) {
      if (window === "60mins_post_match") return "Post-Match";
      if (window === "60mins_post_training") return "Post-Training";
      return window;
    },

    selectMeal(meal) {
      this.$emit("select-meal", meal);
    },
  },

  watch: {
    date() {
      this.loadRecoveryData();
    },
    activity() {
      this.loadRecoveryData();
    },
  },
};
</script>

<style lang="scss" scoped>
.recovery-plan {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.day-label {
  font-size: 0.8rem;
  color: #28a745;
  font-weight: 500;
}

.window-status {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 12px;

  &.active {
    background: #d4edda;
    color: #155724;
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.golden-window {
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #ffc107, #ff9800);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  color: #333;
}

.window-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  font-weight: 700;

  span:first-child {
    font-size: 1.4rem;
    line-height: 1;
  }

  .unit {
    font-size: 0.65rem;
    text-transform: uppercase;
  }
}

.window-info {
  flex: 1;

  h4 {
    font-size: 1rem;
    font-weight: 700;
    margin: 0 0 4px 0;
  }

  p {
    font-size: 0.85rem;
    margin: 0 0 8px 0;
    opacity: 0.9;
  }
}

.countdown {
  font-size: 0.8rem;

  .time {
    font-weight: 700;
    font-size: 1rem;
  }
}

.recovery-priorities,
.recommended-meals,
.recovery-tips,
.full-day-guidance {
  margin-bottom: 24px;

  h4 {
    font-size: 0.85rem;
    font-weight: 600;
    color: #555;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.priority-card {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  border-left: 4px solid #ccc;

  &.carbs {
    border-left-color: #ffc107;
  }

  &.protein {
    border-left-color: #dc3545;
  }

  &.hydration {
    border-left-color: #17a2b8;
  }
}

.priority-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.priority-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: white;

  .carbs & {
    background: linear-gradient(135deg, #ffc107, #ff9800);
  }

  .protein & {
    background: linear-gradient(135deg, #dc3545, #c82333);
  }

  .hydration & {
    background: linear-gradient(135deg, #17a2b8, #138496);
  }
}

.priority-info {
  flex: 1;

  h5 {
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
    margin: 0;
  }

  .target {
    font-size: 0.75rem;
    color: #666;
  }
}

.priority-description {
  font-size: 0.8rem;
  color: #666;
  margin: 0 0 10px 0;
  line-height: 1.4;
}

.progress-bar {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;

  .carbs & {
    background: linear-gradient(90deg, #ffc107, #ff9800);
  }

  .protein & {
    background: linear-gradient(90deg, #dc3545, #c82333);
  }

  .hydration & {
    background: linear-gradient(90deg, #17a2b8, #138496);
  }
}

.meal-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.meal-option {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
    transform: translateX(4px);
  }
}

.meal-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.meal-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.meal-time {
  font-size: 0.75rem;
  color: #888;
}

.meal-macros {
  display: flex;
  gap: 12px;
  margin-bottom: 4px;
}

.macro {
  font-size: 0.75rem;
  font-weight: 500;

  &.carbs {
    color: #cc9a06;
  }

  &.protein {
    color: #a71d2a;
  }
}

.timing-badge {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 600;
  color: #007bff;
  background: rgba(0, 123, 255, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
}

.tips-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tip-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.tip-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #28a745;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.tip-text {
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
}

.guidance-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.guidance-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
}

.guidance-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.guidance-content {
  flex: 1;

  h5 {
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 2px 0;
  }

  p {
    font-size: 0.75rem;
    color: #666;
    margin: 0;
  }
}
</style>
